import React, { ReactElement } from 'react'
import { Card, Tag, Typography, Space, Flex } from 'antd'
import { PiCalendarBlank, PiEye, PiGridFour, PiMapPin, PiPencilSimple } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { IOnboardingLocation, IOnboardingLeaveType, IOnboardingDepartment, OnboardingStep, INTERVAL_TO_I18N_KEY } from './types'
import IntlMessages from '../../util/IntlMessages'

const { Text } = Typography

interface SetupSummaryCardProps {
  currentStep: OnboardingStep
  locations: IOnboardingLocation[]
  leaveTypes: IOnboardingLeaveType[]
  departments: IOnboardingDepartment[]
  savedToDB: boolean
  onEditLocations: () => void
  onEditLeaveTypes: () => void
  onEditDepartments: () => void
}

const STEP_ORDER: Record<OnboardingStep, number> = {
  locations: 0,
  leaveTypes: 1,
  departments: 2,
  import: 3,
}

const SectionHeader = ({
  icon,
  labelKey,
  count,
  onEdit,
  readonly,
}: {
  icon: ReactElement
  labelKey: string
  count: number
  onEdit: () => void
  readonly: boolean
}): ReactElement => {
  const { formatMessage } = useIntl()
  const label = count === 1 ? formatMessage({ id: `${labelKey}.one` }) : formatMessage({ id: `${labelKey}.many` }, { count })

  return (
    <Flex justify="space-between" align="center" className="vt-summary-section-header">
      <Space size={6}>
        {icon}
        <Text strong>{label}</Text>
      </Space>
      <Typography.Link onClick={onEdit} className="vt-summary-edit-link">
        <Space size={4}>
          {readonly ? <PiEye size={12} /> : <PiPencilSimple size={12} />}
          <IntlMessages id={readonly ? 'onboarding.setup.summary.viewLink' : 'onboarding.setup.summary.editLink'} />
        </Space>
      </Typography.Link>
    </Flex>
  )
}

const SetupSummaryCard = ({
  currentStep,
  locations,
  leaveTypes,
  departments,
  savedToDB,
  onEditLocations,
  onEditLeaveTypes,
  onEditDepartments,
}: SetupSummaryCardProps): ReactElement => {
  const { formatMessage } = useIntl()
  const currentIndex = STEP_ORDER[currentStep]

  const showLocations = currentIndex > 0 && locations.length > 0
  const showLeaveTypes = currentIndex > 1 && leaveTypes.length > 0
  const showDepartments = currentIndex > 2 && departments.length > 0

  if (!showLocations && !showLeaveTypes && !showDepartments) return <></>

  const intervalLabel = (interval: string) => formatMessage({ id: INTERVAL_TO_I18N_KEY[interval] ?? INTERVAL_TO_I18N_KEY['FULL-DAY'] })

  return (
    <Card className="vt-setup-summary-card" size="small">
      <div className="vt-summary-label">
        <IntlMessages id="onboarding.setup.summary.title" />
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {showLocations && (
          <div className="vt-summary-section">
            <SectionHeader icon={<PiMapPin size={14} />} labelKey="onboarding.setup.summary.locations" count={locations.length} onEdit={onEditLocations} readonly={savedToDB} />
            <Flex wrap gap={6} style={{ marginTop: 6 }}>
              {locations.map((loc) => (
                <Tag key={loc.id} className="vt-summary-tag" icon={<PiMapPin size={10} />}>
                  {loc.name}
                </Tag>
              ))}
            </Flex>
          </div>
        )}

        {showLeaveTypes && (
          <div className="vt-summary-section">
            <SectionHeader
              icon={<PiCalendarBlank size={14} />}
              labelKey="onboarding.setup.summary.leaveTypes"
              count={leaveTypes.length}
              onEdit={onEditLeaveTypes}
              readonly={savedToDB}
            />
            <Space direction="vertical" style={{ width: '100%', marginTop: 6 }} size={2}>
              {leaveTypes.map((lt) => (
                <Flex key={lt.id} align="center" gap={8} className="vt-summary-leave-row">
                  <div className="vt-summary-leave-bar" style={{ backgroundColor: lt.color }} />
                  <Text strong style={{ fontSize: 13 }}>
                    {lt.name}
                  </Text>
                  {lt.overrides.length > 0 && (
                    <span className="vt-lt-override-badge">
                      {lt.overrides.length === 1
                        ? formatMessage({ id: 'onboarding.setup.leaveTypes.override.one' })
                        : formatMessage({ id: 'onboarding.setup.leaveTypes.override.many' }, { count: lt.overrides.length })}
                    </span>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {lt.hasUnlimitedDays ? formatMessage({ id: 'app.unlimited' }) : `${lt.daysPerYear}d`} ·{' '}
                    {lt.isApprovalRequired ? formatMessage({ id: 'onboarding.setup.approval' }) : formatMessage({ id: 'onboarding.setup.auto' })} ·{' '}
                    {intervalLabel(lt.shortestLeaveIntervalEnum)}
                  </Text>
                </Flex>
              ))}
            </Space>
          </div>
        )}

        {showDepartments && (
          <div className="vt-summary-section">
            <SectionHeader
              icon={<PiGridFour size={14} />}
              labelKey="onboarding.setup.summary.departments"
              count={departments.length}
              onEdit={onEditDepartments}
              readonly={savedToDB}
            />
            <Flex wrap gap={6} style={{ marginTop: 6 }}>
              {departments.map((dept) => (
                <Tag key={dept.id} className="vt-summary-tag">
                  {dept.name}
                </Tag>
              ))}
            </Flex>
          </div>
        )}
      </Space>
    </Card>
  )
}

export default SetupSummaryCard
